import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash, NotePencil, X, Highlighter as HighlighterIcon } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";

export const HL_COLORS = [
  { id: "yellow", label: "Important", dot: "#FACC15" },
  { id: "green", label: "Compris", dot: "#4ADE80" },
  { id: "orange", label: "À revoir", dot: "#FB923C" },
  { id: "pink", label: "À demander", dot: "#F472B6" },
];

const supportsHighlights = () =>
  typeof window !== "undefined" && typeof window.Highlight !== "undefined" && typeof CSS !== "undefined" && !!CSS.highlights;

/** Texte du contenu, espaces normalisés, avec la position DOM (nœud + décalage) de chaque caractère. */
function buildIndex(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let full = "";
  let prevSpace = false;
  const nodes = [];
  const offs = [];
  let n;
  while ((n = walker.nextNode())) {
    if (n.parentElement && n.parentElement.closest(".katex-mathml")) continue;   // doublon MathML des formules
    const t = n.nodeValue;
    for (let i = 0; i < t.length; i++) {
      if (/\s/.test(t[i])) {
        if (prevSpace) continue;
        prevSpace = true;
        full += " ";
      } else {
        prevSpace = false;
        full += t[i];
      }
      nodes.push(n);
      offs.push(i);
    }
  }
  return { full, nodes, offs };
}

/** Ancre d'une sélection : texte + contexte avant/après (pour retrouver le bon passage s'il se répète). */
function anchorFromRange(root, range) {
  const { full, nodes, offs } = buildIndex(root);
  const total = nodes.length;
  if (!total) return null;
  const firstAtOrAfterStart = () => {
    let lo = 0, hi = total;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (range.comparePoint(nodes[mid], offs[mid]) >= 0) hi = mid; else lo = mid + 1;
    }
    return lo;
  };
  const lastBeforeEnd = () => {
    let lo = -1, hi = total - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (range.comparePoint(nodes[mid], offs[mid] + 1) <= 0) lo = mid; else hi = mid - 1;
    }
    return lo;
  };
  let a = firstAtOrAfterStart();
  let b = lastBeforeEnd();
  while (a <= b && full[a] === " ") a++;
  while (b >= a && full[b] === " ") b--;
  if (b - a + 1 < 3) return null;
  return {
    text: full.slice(a, b + 1),
    prefix: full.slice(Math.max(0, a - 40), a),
    suffix: full.slice(b + 1, b + 41),
  };
}

const commonSuffix = (x, y) => { let k = 0; while (k < x.length && k < y.length && x[x.length - 1 - k] === y[y.length - 1 - k]) k++; return k; };
const commonPrefix = (x, y) => { let k = 0; while (k < x.length && k < y.length && x[k] === y[k]) k++; return k; };

/** Retrouve le passage d'un surlignage dans le contenu affiché (null si le texte a changé entre-temps). */
function locate(index, hl) {
  const { full, nodes, offs } = index;
  let best = -1, bestScore = -1;
  let from = 0, i;
  while ((i = full.indexOf(hl.text, from)) !== -1) {
    const score = commonSuffix(full.slice(Math.max(0, i - 60), i), hl.prefix || "") + commonPrefix(full.slice(i + hl.text.length, i + hl.text.length + 60), hl.suffix || "");
    if (score > bestScore) { best = i; bestScore = score; }
    from = i + 1;
  }
  if (best < 0) return null;
  const j = best + hl.text.length - 1;
  const r = document.createRange();
  r.setStart(nodes[best], offs[best]);
  r.setEnd(nodes[j], offs[j] + 1);
  return r;
}

const caretFromPoint = (x, y) => {
  if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y);
    return p ? { node: p.offsetNode, offset: p.offset } : null;
  }
  if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    return r ? { node: r.startContainer, offset: r.startOffset } : null;
  }
  return null;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const isCoarse = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

/**
 * Surlignage et notes sur le texte d'une séquence (étudiant) :
 * sélectionner un passage -> choisir une couleur ; cliquer sur un passage surligné -> note, couleur, suppression.
 */
export function LessonHighlights({ lessonId, containerRef }) {
  const [items, setItems] = useState([]);
  const [toolbar, setToolbar] = useState(null);   // { x, y, anchor }
  const [editor, setEditor] = useState(null);     // { id, x, y }
  const [noteDraft, setNoteDraft] = useState("");
  const rangesRef = useRef([]);                   // [{ id, range }]
  const pointerDown = useRef(false);
  const toolbarRef = useRef(null);
  const editorRef = useRef(null);
  const busyUntil = useRef(0);                    // interaction en cours avec la barre (évite qu'elle disparaisse pendant le tap)
  const supported = supportsHighlights();
  const coarse = isCoarse();

  useEffect(() => {
    let alive = true;
    setItems([]); setToolbar(null); setEditor(null);
    api.get(`/lessons/${lessonId}/highlights`).then((r) => { if (alive) setItems(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [lessonId]);

  // Peinture des surlignages (sans toucher au DOM de React)
  useEffect(() => {
    if (!supported) return undefined;
    const raf = requestAnimationFrame(() => {
      const root = containerRef.current;
      if (!root) return;
      const index = buildIndex(root);
      const located = [];
      items.forEach((h) => {
        const r = locate(index, h);
        if (r) located.push({ id: h.id, color: h.color, range: r });
      });
      rangesRef.current = located;
      HL_COLORS.forEach((c) => {
        const rs = located.filter((l) => l.color === c.id).map((l) => l.range);
        if (rs.length) CSS.highlights.set(`hl-${c.id}`, new window.Highlight(...rs));
        else CSS.highlights.delete(`hl-${c.id}`);
      });
      const target = new URLSearchParams(window.location.search).get("hl");
      const hit = target && located.find((l) => l.id === target);
      if (hit && !window.__hlScrolled) {
        window.__hlScrolled = true;
        hit.range.startContainer.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [items, containerRef, supported, lessonId]);

  useEffect(() => () => {
    if (supportsHighlights()) HL_COLORS.forEach((c) => CSS.highlights.delete(`hl-${c.id}`));
    window.__hlScrolled = false;
  }, [lessonId]);

  // Sélection de texte -> barre de couleurs
  useEffect(() => {
    if (!supported) return undefined;
    let timer = null;
    const evaluate = () => {
      const root = containerRef.current;
      const sel = window.getSelection();
      const keep = () => { if (Date.now() < busyUntil.current) return true; setToolbar(null); return false; };
      if (!root || !sel || sel.isCollapsed || sel.rangeCount === 0) { keep(); return; }
      const range = sel.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) { keep(); return; }
      const anchor = anchorFromRange(root, range);
      if (!anchor) { keep(); return; }
      const rect = range.getBoundingClientRect();
      setEditor(null);
      setToolbar({ x: rect.left + rect.width / 2, y: rect.bottom + 8, anchor });
    };
    const onChange = () => {
      clearTimeout(timer);
      if (pointerDown.current) return;
      timer = setTimeout(evaluate, isCoarse() ? 450 : 300);
    };
    // Souris : on attend le relâchement. Tactile : la sélection se fait par appui long + poignées,
    // le navigateur envoie pointercancel (jamais pointerup) -> on ne bloque donc pas sur le tactile.
    const onDown = (e) => {
      pointerDown.current = e.pointerType === "mouse";
      if (toolbarRef.current && toolbarRef.current.contains(e.target)) busyUntil.current = Date.now() + 1500;
      else if (e.pointerType === "mouse") setToolbar(null);
    };
    const onUp = () => { pointerDown.current = false; clearTimeout(timer); timer = setTimeout(evaluate, 30); };
    const onCancel = () => { pointerDown.current = false; };
    document.addEventListener("selectionchange", onChange);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("selectionchange", onChange);
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
    };
  }, [containerRef, supported]);

  // Clic sur un passage surligné -> éditeur de note
  useEffect(() => {
    if (!supported) return undefined;
    const onClick = (e) => {
      const root = containerRef.current;
      if (!root || !root.contains(e.target)) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return;
      const p = caretFromPoint(e.clientX, e.clientY);
      if (!p) return;
      const hit = rangesRef.current.find((l) => { try { return l.range.isPointInRange(p.node, p.offset); } catch { return false; } });
      if (!hit) { setEditor(null); return; }
      const item = items.find((i) => i.id === hit.id);
      setNoteDraft(item?.note || "");
      setToolbar(null);
      setEditor({ id: hit.id, x: e.clientX, y: e.clientY });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [items, containerRef, supported]);

  // Fermeture de l'éditeur : clic à l'extérieur ou Échap
  useEffect(() => {
    if (!editor) return undefined;
    const onDoc = (e) => { if (editorRef.current && !editorRef.current.contains(e.target)) setEditor(null); };
    const onKey = (e) => { if (e.key === "Escape") setEditor(null); };
    const t = setTimeout(() => document.addEventListener("pointerdown", onDoc), 0);
    document.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); document.removeEventListener("pointerdown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [editor]);

  const create = async (color, openNote = false) => {
    if (!toolbar) return;
    const { anchor, x, y } = toolbar;
    try {
      const { data } = await api.post(`/lessons/${lessonId}/highlights`, { ...anchor, color });
      setItems((prev) => [...prev, data]);
      window.getSelection()?.removeAllRanges();
      setToolbar(null);
      if (openNote) { setNoteDraft(""); setEditor({ id: data.id, x, y: y - 8 }); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const patch = async (id, body) => {
    try {
      const { data } = await api.put(`/highlights/${id}`, body);
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/highlights/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setEditor(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!supported) return null;
  const editing = editor ? items.find((i) => i.id === editor.id) : null;

  return (
    <>
      {items.length === 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="highlight-hint">
          <HighlighterIcon size={14} /> Astuce : sélectionnez un passage du texte pour le surligner ou ajouter une note.
        </p>
      )}

      {toolbar && (
        <div ref={toolbarRef} onMouseDown={(e) => e.preventDefault()}
          className={`fixed z-[70] flex items-center gap-1 rounded-full border border-border bg-card shadow-lg ${coarse ? "gap-3 px-4 py-2.5" : "px-2 py-1.5"}`}
          style={coarse
            ? { left: "50%", transform: "translateX(-50%)", bottom: "calc(16px + env(safe-area-inset-bottom))" }
            : { left: clamp(toolbar.x - 100, 8, window.innerWidth - 208), top: clamp(toolbar.y, 8, window.innerHeight - 52) }}
          data-testid="highlight-toolbar">
          {HL_COLORS.map((c) => (
            <button key={c.id} type="button" title={c.label} onClick={() => create(c.id)}
              className={`${coarse ? "h-9 w-9" : "h-6 w-6"} rounded-full border border-black/10 transition-transform hover:scale-110`} style={{ background: c.dot }} data-testid={`hl-color-${c.id}`} />
          ))}
          <span className="mx-1 h-5 w-px bg-border" />
          <button type="button" title="Surligner et ajouter une note" onClick={() => create("yellow", true)}
            className={`flex ${coarse ? "h-9 w-9" : "h-6 w-6"} items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground`} data-testid="hl-add-note">
            <NotePencil size={coarse ? 22 : 16} />
          </button>
        </div>
      )}

      {editor && editing && (
        <div ref={editorRef} className={`fixed z-[70] rounded-xl border border-border bg-card p-3 shadow-xl ${coarse ? "" : "w-72"}`}
          style={coarse
            ? { left: 8, right: 8, bottom: "calc(8px + env(safe-area-inset-bottom))" }
            : { left: clamp(editor.x - 20, 8, window.innerWidth - 296), top: clamp(editor.y + 14, 8, window.innerHeight - 250) }}
          data-testid="highlight-editor">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {HL_COLORS.map((c) => (
                <button key={c.id} type="button" title={c.label} onClick={() => patch(editing.id, { color: c.id })}
                  className={`${coarse ? "h-8 w-8" : "h-5 w-5"} rounded-full border transition-transform hover:scale-110 ${editing.color === c.id ? "border-foreground ring-2 ring-primary/40" : "border-black/10"}`}
                  style={{ background: c.dot }} />
              ))}
            </div>
            <button type="button" onClick={() => setEditor(null)} className="text-muted-foreground hover:text-foreground" aria-label="Fermer"><X size={16} /></button>
          </div>
          <p className="mb-2 line-clamp-2 border-l-2 border-border pl-2 text-xs italic text-muted-foreground">{editing.text}</p>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={() => { if (noteDraft !== (editing.note || "")) patch(editing.id, { note: noteDraft }); }}
            rows={3}
            placeholder="Votre note sur ce passage..."
            className="w-full resize-none rounded-md border border-input bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            data-testid="highlight-note-input"
            autoFocus={!coarse}
          />
          <div className="mt-2 flex items-center justify-between">
            <button type="button" onClick={() => remove(editing.id)} className="flex items-center gap-1 text-xs text-accent hover:underline" data-testid="highlight-delete">
              <Trash size={13} /> Supprimer le surlignage
            </button>
            <button type="button" onClick={() => { if (noteDraft !== (editing.note || "")) patch(editing.id, { note: noteDraft }); setEditor(null); }}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90">OK</button>
          </div>
        </div>
      )}
    </>
  );
}

/** Note libre de l'étudiant sur la séquence (enregistrement automatique). */
export function LessonNote({ lessonId }) {
  const [content, setContent] = useState("");
  const [state, setState] = useState("idle");   // idle | saving | saved
  const loaded = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    loaded.current = false;
    setContent(""); setState("idle");
    api.get(`/lessons/${lessonId}/note`).then((r) => { if (alive) { setContent(r.data.content || ""); loaded.current = true; } }).catch(() => {});
    return () => { alive = false; clearTimeout(timer.current); };
  }, [lessonId]);

  const onChange = (e) => {
    const v = e.target.value;
    setContent(v);
    if (!loaded.current) return;
    setState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.put(`/lessons/${lessonId}/note`, { content: v }).then(() => setState("saved")).catch((err) => { setState("idle"); toast.error(apiError(err)); });
    }, 800);
  };

  return (
    <div className="mt-6 rounded-lg border border-border p-4" data-testid="lesson-note">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><NotePencil size={14} /> Ma note sur cette séquence</p>
        <span className="text-xs text-muted-foreground">{state === "saving" ? "Enregistrement..." : state === "saved" ? "Enregistré ✓" : "Visible par vous seul"}</span>
      </div>
      <textarea value={content} onChange={onChange} rows={3} placeholder="Vos idées, questions, liens avec d'autres cours..."
        className="w-full resize-y rounded-md border border-input bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-ring" data-testid="lesson-note-input" />
    </div>
  );
}
